import { useState, useEffect } from 'react'
import {
  MdFlight,
  MdAdd,
  MdSchedule,
  MdRefresh,
  MdWarning,
  MdCheckCircle,
  MdEngineering,
  MdBusiness,
} from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  LoadingSpinner,
  SearchBar,
  FilterBar,
  StatCard,
  EmptyState,
} from '../../components/ui'
import { useAircraft } from '../../hooks/useAircraft'
import { useWebsocket } from '../../hooks/useWebsocket'
import AircraftList from '../../modules/aircraft/AircraftList'
import {
  addSubscription,
  removeSubscription,
  selectAircraftListUpdated,
  setAircraftListUpdated,
} from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function AircraftPage() {
  const dispatch = useDispatch()
  const [aircraft, setAircraft] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const { getAircraftAsync, isGetAircraftLoading } = useAircraft()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    maintenance: 0,
    retired: 0,
    narrowBody: 0,
    wideBody: 0,
    regional: 0,
  })
  const aircraftListUpdated = useSelector(selectAircraftListUpdated)

  const loadAircraft = async () => {
    try {
      setIsLoading(true)
      const aircraftData = await getAircraftAsync()
      setAircraft(aircraftData || [])

      // Calculate statistics
      const total = aircraftData.length
      const active = aircraftData.filter((a) => a.status === 'active').length
      const inactive = aircraftData.filter((a) => a.status === 'inactive').length
      const maintenance = aircraftData.filter((a) => a.status === 'maintenance').length
      const retired = aircraftData.filter((a) => a.status === 'retired').length
      const narrowBody = aircraftData.filter((a) => a.type === 'Narrow-body').length
      const wideBody = aircraftData.filter((a) => a.type === 'Wide-body').length
      const regional = aircraftData.filter((a) => a.type === 'Regional').length

      setStats({
        total,
        active,
        inactive,
        maintenance,
        retired,
        narrowBody,
        wideBody,
        regional,
      })
    } catch (error) {
      console.error('Error loading aircraft:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadAircraft()

    const channelName = getWebsocketChannel().aircraftList

    if (channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (aircraftListUpdated) {
      setAircraft((prevAircraft) => {
        const existingIndex = prevAircraft.findIndex(
          (aircraft) => aircraft._id.toString() === aircraftListUpdated._id.toString(),
        )
        if (existingIndex !== -1) {
          const updatedAircraft = [...prevAircraft]
          updatedAircraft[existingIndex] = aircraftListUpdated
          return updatedAircraft
        } else {
          return [...prevAircraft, aircraftListUpdated]
        }
      })
      dispatch(setAircraftListUpdated(undefined))
    }
  }, [aircraftListUpdated]) // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredAircraft = () => {
    let filtered = aircraft

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((a) => a.status === statusFilter)
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter((a) => a.type === typeFilter)
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (a) =>
          a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.airline?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.maker?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.model?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    return filtered
  }

  const filteredAircraft = getFilteredAircraft()

  const handleAddAircraft = () => {
    navigate('/aircraft/new')
  }

  const handleRefresh = () => {
    loadAircraft()
  }

  const handleViewAircraft = (aircraft) => {
    navigate(`/aircraft/${aircraft._id}`)
  }

  const handleEditAircraft = (aircraft) => {
    navigate(`/aircraft/${aircraft._id}/edit`)
  }

  const handleDeleteAircraft = (aircraft) => {
    // TODO: Implement delete confirmation modal
    console.log('Delete aircraft:', aircraft._id)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              Aircraft Management
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Monitor and manage aircraft fleet operations and maintenance
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={isGetAircraftLoading}
              className="flex items-center space-x-2"
            >
              <MdRefresh className={isGetAircraftLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>
            <Button
              variant="primary"
              onClick={handleAddAircraft}
              className="flex items-center space-x-2"
            >
              <MdAdd className="text-lg" />
              <span>Add Aircraft</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Aircraft"
          value={stats.total}
          subtitle="Fleet size"
          icon={MdFlight}
          iconBgColor="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
        />
        <StatCard
          title="Active Aircraft"
          value={stats.active}
          subtitle="Currently operating"
          icon={MdCheckCircle}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />
        <StatCard
          title="In Maintenance"
          value={stats.maintenance}
          subtitle="Under maintenance"
          icon={MdEngineering}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
        />
        <StatCard
          title="Inactive Aircraft"
          value={stats.inactive}
          subtitle="Temporarily grounded"
          icon={MdWarning}
          iconBgColor="bg-error-100 dark:bg-error-900"
          iconColor="text-error-600 dark:text-error-400"
        />
      </div>

      {/* Additional Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Narrow-body"
          value={stats.narrowBody}
          subtitle="Single-aisle aircraft"
          icon={MdFlight}
          iconBgColor="bg-info-100 dark:bg-info-900"
          iconColor="text-info-600 dark:text-info-400"
        />
        <StatCard
          title="Wide-body"
          value={stats.wideBody}
          subtitle="Twin-aisle aircraft"
          icon={MdBusiness}
          iconBgColor="bg-purple-100 dark:bg-purple-900"
          iconColor="text-purple-600 dark:text-purple-400"
        />
        <StatCard
          title="Regional"
          value={stats.regional}
          subtitle="Short-haul aircraft"
          icon={MdSchedule}
          iconBgColor="bg-indigo-100 dark:bg-indigo-900"
          iconColor="text-indigo-600 dark:text-indigo-400"
        />
      </div>

      {/* Filters and Search */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Aircraft ({filteredAircraft.length})
          </h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search aircraft..."
            />
            <div className="flex items-center space-x-3">
              <FilterBar
                filterValue={statusFilter}
                onFilterChange={(e) => setStatusFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: `Active (${stats.active})` },
                  { value: 'inactive', label: `Inactive (${stats.inactive})` },
                  { value: 'maintenance', label: `Maintenance (${stats.maintenance})` },
                  { value: 'retired', label: `Retired (${stats.retired})` },
                ]}
              />
              <FilterBar
                filterValue={typeFilter}
                onFilterChange={(e) => setTypeFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Types' },
                  { value: 'Narrow-body', label: `Narrow-body (${stats.narrowBody})` },
                  { value: 'Wide-body', label: `Wide-body (${stats.wideBody})` },
                  { value: 'Regional', label: `Regional (${stats.regional})` },
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Aircraft List */}
      {filteredAircraft.length === 0 && !isLoading ? (
        <EmptyState
          icon={MdFlight}
          title="No aircraft found"
          description={
            searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'No aircraft match your current filters. Try adjusting your search criteria.'
              : 'Get started by adding your first aircraft to the fleet.'
          }
          action={
            searchTerm || statusFilter !== 'all' || typeFilter !== 'all' ? (
              <Button
                variant="primary"
                onClick={() => {
                  setSearchTerm('')
                  setStatusFilter('all')
                  setTypeFilter('all')
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={handleAddAircraft}>
                Add Aircraft
              </Button>
            )
          }
        />
      ) : (
        <AircraftList
          aircraft={filteredAircraft}
          onViewAircraft={handleViewAircraft}
          onEditAircraft={handleEditAircraft}
          onDeleteAircraft={handleDeleteAircraft}
        />
      )}
    </div>
  )
}

export default AircraftPage
